import React, { useEffect, useState } from 'react';
import axios from '../utils/axios';
import logger from '../utils/logger';
import './NotificationManager.css';

interface NotificationManagerProps {
  vapidPublicKey: string;
}

const NotificationManager: React.FC<NotificationManagerProps> = ({ vapidPublicKey }) => {
  const [permission, setPermission] = useState<NotificationPermission>(Notification.permission);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  const availableTopics = [
    { id: 'new-sessions', label: 'New Sessions Available', description: 'Get notified when new crossword sessions are created' },
    { id: 'invitations', label: 'Session Invitations', description: 'Get notified when someone invites you to a session' },
  ];

  const handleTopicChange = (topicId: string, checked: boolean) => {
    setSelectedTopics(prev => 
      checked 
        ? [...prev, topicId]
        : prev.filter(id => id !== topicId)
    );
  };

  // Utility function to convert VAPID public key to Uint8Array
  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  useEffect(() => {
    // Check for Service Worker and Push API support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setMessage('Push notifications are not supported by your browser.');
      return;
    }

    // Check current subscription status
    const checkSubscription = async () => {
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      }
    };
    checkSubscription();
  }, [permission]);

  const registerServiceWorker = async () => {
    try {
      // Register the service worker from the public directory
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      logger.info('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      logger.error('Service Worker registration failed:', error);
      setMessage('Failed to register service worker.');
      throw error; // Re-throw to prevent further execution if registration fails
    }
  };

  const subscribeUser = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      const registration = await registerServiceWorker(); // Ensure SW is registered

      const subscribeOptions = {
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      };

      const subscription = await registration.pushManager.subscribe(subscribeOptions);
      logger.info('User is subscribed:', subscription);

      // Send subscription to your backend - manually extract the properties
      await axios.post('/api/subscribe', {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: subscription.getKey('p256dh') ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))) : '',
          auth: subscription.getKey('auth') ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))) : ''
        },
        topics: selectedTopics
      });

      setIsSubscribed(true);
      setMessage('Successfully subscribed to notifications!');
    } catch (error: unknown) {
      logger.error('Failed to subscribe the user:', error);
      
      // Handle axios errors
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          setMessage('Authentication required. Please log in and try again.');
        } else {
          setMessage(`Failed to send subscription to server: ${axiosError.response?.status || 'Unknown error'}`);
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setMessage(`Failed to subscribe: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeUser = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        logger.info('User is unsubscribed.');

        // Optionally, inform your backend about the unsubscription
        await axios.post('/api/unsubscribe', { 
          endpoint: subscription.endpoint 
        });

        setIsSubscribed(false);
        setMessage('Successfully unsubscribed from notifications.');
      } else {
        setMessage('No active subscription found.');
      }
    } catch (error: unknown) {
      logger.error('Failed to unsubscribe the user:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setMessage(`Failed to unsubscribe: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      setMessage('This browser does not support notifications.');
      return;
    }

    if (permission === 'denied') {
      setMessage('Notification permission was denied. Please enable it in your browser settings.');
      return;
    }

    // Best practice: Only request permission on a user gesture
    const result = await Notification.requestPermission();
    setPermission(result);

    if (result === 'granted') {
      setMessage('Notification permission granted. You can now subscribe.');
      subscribeUser(); // Automatically subscribe after permission is granted
    } else {
      setMessage('Notification permission denied.');
    }
  };

  return (
    <div className="notification-manager">
      <h2 className="notification-manager__title">Push Notifications</h2>
      <p className="notification-manager__message">{message}</p>

      {/* Topic Selection */}
      {permission === 'granted' && !isSubscribed && (
        <div className="topic-selection">
          <h3 className="topic-selection__title">Choose notification types:</h3>
          <div className="topic-list">
            {availableTopics.map(topic => (
              <div key={topic.id} className="topic-item">
                <label className="topic-item__label">
                  <input
                    type="checkbox"
                    checked={selectedTopics.includes(topic.id)}
                    onChange={(e) => handleTopicChange(topic.id, e.target.checked)}
                    className="topic-item__checkbox"
                  />
                  <div className="topic-item__content">
                    <div className="topic-item__title">{topic.label}</div>
                    <div className="topic-item__description">{topic.description}</div>
                  </div>
                  {selectedTopics.includes(topic.id) && (
                    <div className="topic-item__checkmark">
                      <svg className="topic-item__checkmark-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </label>
              </div>
            ))}
          </div>
          {selectedTopics.length > 0 && (
            <div className="selected-count">
              <p className="selected-count__text">
                <span className="selected-count__emphasis">{selectedTopics.length} notification type{selectedTopics.length !== 1 ? 's' : ''} selected</span>
              </p>
            </div>
          )}
        </div>
      )}

      {permission === 'default' && (
        <button
          onClick={requestPermission}
          disabled={isLoading}
          className="notification-btn notification-btn--primary"
        >
          {isLoading ? 'Requesting...' : 'Enable Notifications'}
        </button>
      )}

      {permission === 'granted' && !isSubscribed && (
        <button
          onClick={subscribeUser}
          disabled={isLoading || selectedTopics.length === 0}
          className="notification-btn notification-btn--success"
        >
          {isLoading ? 'Subscribing...' : `Subscribe to ${selectedTopics.length} notification types`}
        </button>
      )}

      {permission === 'granted' && isSubscribed && (
        <button
          onClick={unsubscribeUser}
          disabled={isLoading}
          className="notification-btn notification-btn--danger"
        >
          {isLoading ? 'Unsubscribing...' : 'Unsubscribe from Notifications'}
        </button>
      )}

      {permission === 'denied' && (
        <p className="notification-error">
          Notifications are blocked. Please enable them in your browser settings.
        </p>
      )}
    </div>
  );
};

export default NotificationManager;
